import React from 'react';
import { ArrowRight, MapPin, Calendar, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { getFutureDatesSorted, isDateUnavailable, isPackageClosed } from '../utils/occupancy';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (imagePath) => {
  if (!imagePath) return '/placeholder-package.jpg';
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/uploads')) return `${BACKEND_URL}${imagePath}`;
  return imagePath;
};

const OccupancyBar = ({ percentage, isSoldOut }) => {
  if (isSoldOut) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-14 h-1.5 bg-red-200 rounded-full overflow-hidden">
          <div className="h-full bg-red-500 rounded-full" style={{ width: '100%' }} />
        </div>
        <span className="text-[10px] font-bold text-red-600">Esgotado</span>
      </div>
    );
  }
  const color = percentage >= 70 ? 'bg-orange-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-green-500';
  const trackColor = percentage >= 70 ? 'bg-orange-100' : percentage >= 50 ? 'bg-yellow-100' : 'bg-green-100';
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-14 h-1.5 ${trackColor} rounded-full overflow-hidden`}>
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="text-[10px] font-semibold text-gray-600">{percentage}% vendido</span>
    </div>
  );
};

const DaysCountdown = ({ date }) => {
  if (!date?.start_date) return null;
  const now = new Date(); now.setHours(0,0,0,0);
  const target = new Date(date.start_date.split('T')[0] + 'T00:00:00');
  const days = Math.ceil((target - now) / 86400000);
  if (days < 0) return null;
  const color = days <= 7 ? 'text-red-600' : days <= 30 ? 'text-orange-600' : 'text-gray-600';
  return (
    <div className="flex items-center gap-1">
      <Clock className="w-3 h-3" />
      <span className={`text-[10px] font-semibold ${color}`}>
        {days === 0 ? 'Hoje!' : days === 1 ? 'Amanhã' : `Faltam ${days} dias`}
      </span>
    </div>
  );
};

const PackageCard = ({ packageData, cardIndicator = 'percentage' }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'nacional-aereo': 'Nacional - Aéreo',
      'internacional-aereo': 'Internacional - Aéreo',
      'rodoviario': 'Rodoviário',
      'passeio-1-dia': 'Passeio de 1 Dia'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'nacional-aereo': 'bg-blue-100 text-blue-800',
      'internacional-aereo': 'bg-purple-100 text-purple-800',
      'rodoviario': 'bg-orange-100 text-orange-800',
      'passeio-1-dia': 'bg-green-100 text-green-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const futureDates = getFutureDatesSorted(packageData.dates);
  const isSoldOut = isPackageClosed(packageData.dates);
  const packageUrl = `/pacote/${packageData.slug || packageData.id}`;

  return (
    <Card data-testid={`package-card-${packageData.id}`} className="group overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col">
      <Link to={packageUrl} className="relative overflow-hidden h-36 sm:h-48 md:h-56 block cursor-pointer">
        <img
          src={getImageUrl(packageData.main_image || packageData.image)}
          alt={packageData.name}
          className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${isSoldOut ? 'grayscale' : ''}`}
          onError={(e) => { e.target.src = '/placeholder-package.jpg'; }}
        />
        <Badge className={`absolute top-2 right-2 text-xs ${getCategoryColor(packageData.category)}`}>
          {getCategoryLabel(packageData.category)}
        </Badge>
        {packageData.duration_days && (
          <Badge className="absolute top-2 left-2 bg-white/90 text-gray-800 text-xs font-semibold">
            {packageData.duration_days} dias
          </Badge>
        )}
        {isSoldOut && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <Badge data-testid="package-closed-badge" className="bg-red-600 text-white font-bold text-sm px-3 py-1">ENCERRADO</Badge>
          </div>
        )}
      </Link>

      <CardContent className="p-2 sm:p-4 md:p-6 flex-grow flex flex-col">
        <div className="space-y-1 sm:space-y-2 md:space-y-3 flex-grow">
          <Link to={packageUrl}>
            <h3 className="text-base sm:text-lg md:text-xl font-extrabold text-gray-900 leading-tight line-clamp-2 hover:text-theme-primary transition-colors" style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif" }}>
              {packageData.name}
            </h3>
          </Link>

          <div className="flex flex-wrap items-baseline space-x-1 md:space-x-2">
            {packageData.discount_percentage > 0 && packageData.original_price ? (
              <>
                <span className="text-xs md:text-sm text-gray-400 line-through">
                  {formatPrice(packageData.original_price)}
                </span>
                <span className="text-sm sm:text-base md:text-lg font-bold text-green-600">
                  {formatPrice(packageData.price)}
                </span>
                <span className="text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded font-bold">
                  -{packageData.discount_percentage}%
                </span>
              </>
            ) : (
              <>
                <span className="text-sm sm:text-base md:text-lg font-bold text-theme-primary">
                  {formatPrice(packageData.price)}
                </span>
                <span className="text-xs md:text-sm text-gray-500 hidden sm:inline">por pessoa</span>
              </>
            )}
          </div>

          <div className="space-y-1 md:space-y-2">
            <div className="flex items-center space-x-1 md:space-x-2 text-xs md:text-sm text-gray-600">
              <Calendar className="w-3 h-3 md:w-4 md:h-4" />
              <span className="font-semibold">Datas:</span>
            </div>
            <div className="space-y-1 md:space-y-1.5">
              {futureDates.slice(0, 2).map((date) => {
                const dateSoldOut = isDateUnavailable(date);
                return (
                  <div key={date.id} className="flex items-center justify-between gap-2">
                    <span className={`text-xs md:text-sm ${dateSoldOut ? 'text-red-600 line-through' : 'text-gray-700'}`}>
                      {date.date}
                    </span>
                    {dateSoldOut && (
                      <span className="text-[10px] font-bold text-red-600">Encerrado</span>
                    )}
                  </div>
                );
              })}
              {futureDates.length > 2 && (
                <span className="text-xs text-gray-500">
                  +{futureDates.length - 2} data(s)
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-1 md:space-x-2 text-xs md:text-sm text-gray-600 hidden sm:flex">
            <MapPin className="w-3 h-3 md:w-4 md:h-4" />
            <span className="truncate">{packageData.departure}</span>
          </div>
        </div>

        <Link
          to={packageUrl}
          className="mt-2 sm:mt-4 md:mt-6 w-full btn-primary-theme font-semibold py-2 md:py-3 px-3 md:px-6 rounded-lg flex items-center justify-center space-x-1 md:space-x-2 transition-all duration-300 group/btn text-xs sm:text-sm md:text-base"
          data-testid="package-card-details-btn"
        >
          <span>Ver Detalhes</span>
          <ArrowRight className="w-3 h-3 md:w-4 md:h-4 group-hover/btn:translate-x-1 transition-transform" />
        </Link>
      </CardContent>
    </Card>
  );
};

export default PackageCard;
